import React, { useState, useEffect, useCallback } from 'react';
import { useRef, useLayoutEffect } from 'react';
import { useAuth, refreshOnce } from './auth.js';
import styled from 'styled-components';
import { signOutRedirect } from './aws-config.js';
import Button from './components/Button.jsx';
import ErrorBoundary from './components/ErrorBoundary.js';
import Landing from './components/Landing.js';
import PreferencesDialog from './components/PreferencesDialog.jsx';
import AccountDialog from './components/AccountDialog.jsx';
import UsersDialog from './components/UsersDialog.jsx';
import Subscription from './components/Subscription.jsx';
import { redirectToSubscriptionPage } from './services/subscription.js';
import './App.css';
import { ThemeProvider } from 'styled-components';
import ProjectDashboard from './components/ProjectDashboard.jsx';
import RequirementsPage from './components/RequirementsPage.jsx';
import SlackCallback from './components/SlackCallback.jsx';
import GoogleDriveIntegration from './components/GoogleDriveIntegration.jsx';
import documentChunkingProgressService from './services/documentChunkingProgressService';
import { Modal } from 'antd';
import ProjectWizard from './components/ProjectWizard.jsx';
import ProjectService from './services/projectService';
import documentProgressService from './services/documentProgressService';
import AppTour from './components/AppTour.jsx';
import OnboardingChecklist from './components/OnboardingChecklist.jsx';
import './styles/AppTour.css';
import { setOnboardingUserId } from './utils/onboardingProgress';
import { LanguageProvider, useFlexibleTranslation } from './contexts/LanguageContext';
import TopNav from './components/TopNav';
import ProjectSettings from './components/ProjectSettings.jsx';
import ComplianceDashboard from './components/ComplianceDashboard.jsx';
import CommunicationsDashboard from './components/CommunicationsDashboard.jsx';
import HostedAuth from "./components/HostedAuth";
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ConfirmSignup from './components/ConfirmSignup.jsx';
import SignIn from './components/SignIn.jsx';
import OidcCallback from './components/OidcCallback.jsx';
import Paywall from './components/Paywall.jsx';
import BillingAdminRedirect from './components/BillingAdminRedirect.jsx';
import CompanyService, { isExpired } from './services/companyService.js';
import CompanyResourcesPage from './components/CompanyResourcesPage.jsx';
import { NotificationsProvider } from './contexts/NotificationsContext.js';
import NotificationSystem from './components/NotificationSystem.jsx';
import ZoneProfilePanel from './components/ZoneProfilePanel.jsx';
import { cachePendingZoneProfile, clearCachedPendingZoneProfile } from './services/zoneProfileService.js';
import { useNotificationsContext } from './contexts/NotificationsContext.js';
import ExtractedRequirementsPage from './components/ExtractedRequirementsPage.jsx';
import ReviewPage from './components/drawing-ui/ReviewPage.jsx';
import { AlertDialogProvider, useAlertDialog } from './contexts/AlertDialogContext';
import { phProjectCreated, phSetActiveProject, phRegister } from './utils/posthogEvents';
import useProductUsageHeartbeat from './hooks/useProductUsageHeartbeat.js';
import MicrosoftAdminConsentComplete from './components/MicrosoftAdminConsentComplete.jsx';
import ReconcileDeliverables from './components/ReconcileDeliverables.jsx';

function GlobalNotificationBell({ setCurrentPage }) {
  const { notifications, toasts, unreadCount, jobProgress, jobMeta, dismissToast, dismissNotif, markAllRead } = useNotificationsContext();
  const auth = useAuth();
  const [zonePanel, setZonePanel] = useState(null);

  // Passively remember any pending zone-confirm setup we see in a notification,
  // so the Requirements Register can still offer a resume if the notification is
  // later dismissed/lost (stopgap until the server /pending route is wired).
  useEffect(() => {
    (notifications || []).forEach(n => {
      if (n?.actionType === 'zone_confirm' && n.actionPayload?.projectId) {
        cachePendingZoneProfile(n.actionPayload);
      }
    });
  }, [notifications]);

  return (
    <>
      <NotificationSystem
        notifications={notifications}
        toasts={toasts}
        unreadCount={unreadCount}
        jobProgress={jobProgress}
        jobMeta={jobMeta}
        dismissToast={dismissToast}
        dismissNotif={dismissNotif}
        markAllRead={markAllRead}
        onAction={(n) => {
          if (n?.actionType === 'zone_confirm') {
            setZonePanel({ ...(n.actionPayload || {}), notificationId: n.notificationId });
          } else if (n?.actionType === 'continue_extraction') {
            ProjectService.continueExtraction(n.actionPayload || {})
              .then(() => dismissNotif(n.notificationId))
              .catch(e => console.error('[continueExtraction] failed:', e));
          } else if (n?.actionType === 'reconcile') {
            setCurrentPage('reconcile-deliverables');
          }
        }}
      />
      {zonePanel && (
        <ZoneProfilePanel
          payload={zonePanel}
          userId={auth?.user?.profile?.sub || auth?.user?.sub || ''}
          onClose={() => setZonePanel(null)}
          onDone={() => clearCachedPendingZoneProfile(zonePanel.projectId)}
          onDismissStale={() => {
            dismissNotif(zonePanel.notificationId);
            clearCachedPendingZoneProfile(zonePanel.projectId);
            setZonePanel(null);
          }}
        />
      )}
    </>
  );
}


function ReconcileGate({ setCurrentPage, projectId }) {
  const { notifications } = useNotificationsContext();
  const pendingNotif = (notifications || []).find((n) => n?.actionType === 'reconcile');
  const [dismissed, setDismissed] = useState(false);


  useEffect(() => {
    if (pendingNotif) setDismissed(false);
  }, [pendingNotif?.notificationId, pendingNotif?.pendingCount]);

  if (!pendingNotif || dismissed) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: '#fff', overflowY: 'auto' }}>
      <ReconcileDeliverables
        projectId={projectId}
        gateMode
        onComplete={() => { setDismissed(true); setCurrentPage('requirements'); }}
      />
    </div>
  );
}


const AppContent = ({
  auth, 
  preferences, 
  isPreferencesOpen, 
  setIsPreferencesOpen, 
  isAccountOpen, 
  setIsAccountOpen, 
  isLoading, 
  selectedProject, 
  setSelectedProject, 
  projects, 
  currentPage, 
  setCurrentPage, 
  isDriveModalOpen, 
  setIsDriveModalOpen, 
  selectedBlockForDrive, 
  setSelectedBlockForDrive, 
  showProjectWizard, 
  setShowProjectWizard, 
  projectWasCreated,
  setProjectWasCreated,
  isUsersOpen, 
  setIsUsersOpen, 
  isFirstVisit, 
  projectsLoaded, 
  handleSignOut, 
  handlePreferencesSave, 
  handleNewProject, 
  handleCreateProject, 
  renderPage,
  chunkingProgress,
  navRef,
  startTourRef,
  sandboxMode,
  canSeeCompanyResources,
  projectSettingsDirty,
  setProjectSettingsDirty,
  myMembership,
  company,
  planKey,
}) => {
    const { t } = useFlexibleTranslation();
    const { showConfirm } = useAlertDialog();
    const chunkingStatus = React.useMemo(() => {
    const active = !!chunkingProgress?.isVisible;
    if (!active) return { active: false };

    const percent = Math.max(0, Math.min(100, Number(chunkingProgress?.overallProgress) || 0));
    const stageRaw = String(chunkingProgress?.currentStage || '');
    const title = chunkingProgress?.documentTitle || 'Processing documents';

    const stageOrder = [
      'Processing Document',
      'Breaking it Down',
      'Understanding the Content',
      'Organizing for Search',
    ];

    const guessIndex = () => {
      const s = stageRaw.toLowerCase();
      if (s.includes('processing')) return 0;
      if (s.includes('breaking')) return 1;
      if (s.includes('understanding')) return 2;
      if (s.includes('organizing') || s.includes('index')) return 3;
      return 1;
    };

    const activeIdx = guessIndex();

    const steps = stageOrder.map((label, idx) => ({
      label,
      state: idx < activeIdx ? 'done' : idx === activeIdx ? 'active' : 'todo',
      detail: idx === activeIdx ? stageRaw : undefined,
    }));

    return { active: true, percent, title, steps };
  }, [chunkingProgress]);


  if (isLoading) {
    return (
      <LoadingScreen>
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
          <path d="M12 4c4.41 0 8 3.59 8 8h2c0-5.52-4.48-10-10-10v2z">
            <animateTransform
              attributeName="transform"
              attributeType="XML"
              type="rotate"
              from="0 12 12"
              to="360 12 12"
              dur="1s"
              repeatCount="indefinite"
            />
          </path>
        </svg>
      </LoadingScreen>
    );
  }

  if (auth.error) {
    return (
      <ErrorScreen>
        <Logo 
          src="/CODiii_PrimaryGradient1.png" 
          alt="CODiii Logo"
          style={{ width: '180px' }}
        />
        <ErrorMessage>
          <h2>Authentication Error</h2>
          <p>{auth.error.message}</p>
          <Button onClick={() => window.location.reload()}>
            Try Again
          </Button>
        </ErrorMessage>
      </ErrorScreen>
    );
  }

  return (
    <NotificationsProvider projectId={selectedProject?.id}>
    <ThemeProvider theme={preferences.theme === 'dark' ? darkTheme : lightTheme}>
      <ErrorBoundary>
        <AppContainer>
          <AppTour
            startTourRef={startTourRef}
            isFirstVisit={isFirstVisit}
            selectedProject={selectedProject}
            projects={projects}
            projectsLoaded={projectsLoaded}
            currentPage={currentPage}
            onCreateProject={handleNewProject}
            planKey={planKey}
            userId={auth.user?.sub}
          />

          <OnboardingChecklist
            planKey={planKey}
            onResumeTour={() => startTourRef.current?.()}
            userId={auth.user?.sub}
          />

          <TopNav
            ref={navRef}
            sandboxMode={sandboxMode}
            user={{
              name: auth.user?.profile?.name || 'User',
              email: auth.user?.profile?.email || ''
            }}
            projects={projects}
            selectedProject={selectedProject}
            onSelectProject={setSelectedProject}
            activeTab={
              currentPage === 'dashboard'              ? 'Chat' :
              currentPage === 'requirements'           ? 'Requirements' :
              currentPage === 'requirements-v2'        ? 'RequirementsLab' :
              currentPage === 'extracted-requirements' ? 'ExtractedRequirements' :
              currentPage === 'compliance'             ? 'Compliance' :
              currentPage === 'communications'       ? 'Communications' :
              currentPage === 'review'               ? 'Review' :
              currentPage === 'company-resources'      ? 'CompanyResources' :
              currentPage === 'reconcile-deliverables' ? 'Requirements' :
              'Settings'
            }
            onTabChange={async (tab) => {
              if (sandboxMode) {
                setCurrentPage('dashboard');
                return;
              }

              if (currentPage === 'project-settings' && projectSettingsDirty) {
                const ok = await showConfirm({
                  type: 'warning',
                  title: 'Unsaved changes',
                  message: 'You have unsaved changes in Project Settings. Leave anyway?',
                  confirmText: 'Leave',
                  cancelText: 'Stay',
                });
                if (!ok) return;
                setProjectSettingsDirty(false);
              }

              if (tab === 'Chat') setCurrentPage('dashboard');
              if (tab === 'Requirements') setCurrentPage('requirements');
              if (tab === 'RequirementsLab') setCurrentPage('requirements-v2');
              if (tab === 'ExtractedRequirements') setCurrentPage('extracted-requirements');
              if (tab === 'Compliance') setCurrentPage('compliance');
              if (tab === 'Communications') setCurrentPage('communications');
              if (tab === 'Review') setCurrentPage('review');
              if (tab === 'CompanyResources') setCurrentPage('company-resources');
              if (tab === 'Settings') setCurrentPage('project-settings');
            }}
            onSignOut={handleSignOut}
            onCreateProject={handleNewProject}
            onOpenAccount={() => setIsAccountOpen(true)}
            canSeeCompanyResources={canSeeCompanyResources}
            planKey={planKey}
            chunkingStatus={chunkingStatus}
          />

          <Main 
              $scroll={currentPage === 'project-settings'}
              $innerScroll={currentPage === 'compliance' || currentPage === 'communications' || currentPage === 'review' || currentPage === 'project-settings' || currentPage === 'requirements'}
            >
            {renderPage()}
          </Main>

          <PreferencesDialog
            isOpen={isPreferencesOpen}
            onClose={() => setIsPreferencesOpen(false)}
            preferences={preferences}
            onSave={handlePreferencesSave}
          />
          <AccountDialog
            isOpen={isAccountOpen}
            onClose={() => setIsAccountOpen(false)}
            user={auth.user}
            onSignOut={handleSignOut}
            membership={myMembership}
            company={company}
          />
          <UsersDialog
            isOpen={isUsersOpen}
            onClose={() => setIsUsersOpen(false)}
            currentUser={auth.user}
            selectedProject={selectedProject}
          />

          <Modal
            title={t('common.googleDriveIntegration', 'Google Drive Integration')}
            open={isDriveModalOpen}
            onCancel={() => {
              setIsDriveModalOpen(false);
              setSelectedBlockForDrive(null);
            }}
            footer={null}
            width={600}
          >
            {selectedBlockForDrive && (
              <GoogleDriveIntegration
                blockId={selectedBlockForDrive}
                onFolderLinked={() => {
                  setIsDriveModalOpen(false);
                  setSelectedBlockForDrive(null);
                }}
              />
            )}
          </Modal>
          {showProjectWizard && (
            <ProjectWizard
              isOpen={showProjectWizard}
              onClose={() => {
                setShowProjectWizard(false);
                if (projectWasCreated) setCurrentPage('requirements');
                setProjectWasCreated(false);
              }}
              onProjectCreated={async (newProject) => {
                await handleCreateProject(newProject);
                setProjectWasCreated(true);
              }}
            />
          )}
          <GlobalNotificationBell setCurrentPage={setCurrentPage} />
          <ReconcileGate setCurrentPage={setCurrentPage} projectId={selectedProject?.id} />
        </AppContainer>
      </ErrorBoundary>
    </ThemeProvider>
    </NotificationsProvider>
  );
};

const lightTheme = {
  background: '#ffffff',
  text: '#333333',
  primary: '#2196f3',
  secondary: '#718096',
  border: '#e0e0e0',
  shadow: 'rgba(0, 0, 0, 0.1)',
};

const darkTheme = {
  background: '#1a1a1a',
  text: '#ffffff',
  primary: '#2196f3',
  secondary: '#a0aec0',
  border: '#333333',
  shadow: 'rgba(0, 0, 0, 0.3)',
};

const AppContainer = styled.div`
  background-color: ${props => props.theme.background};
  min-height: 100vh;
  position: relative;
  color: ${props => props.theme.text};
  display: flex;
  flex-direction: column;
  overflow: clip;
`;

const Logo = styled.img`
  height: 40px;
`;


const LoadingScreen = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%);

  svg {
    width: 40px;
    height: 40px;
    color: #ff6b35;
  }
`;

const ErrorScreen = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%);
  text-align: center;
`;

const ErrorMessage = styled.div`
  background-color: #fff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 480px;
  width: 100%;
  margin-top: 24px;

  h2 {
    color: #e53e3e;
    margin: 0 0 16px;
  }

  p {
    color: #718096;
    margin: 0 0 24px;
  }
`;

const Main = styled.main`
  padding-top: calc(var(--topnav-height, 90px));
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: ${props => props.$innerScroll ? 'hidden' : props.$scroll ? 'auto' : 'hidden'};
  display: flex;
  flex-direction: column;
  height: ${props => props.$innerScroll 
    ? 'calc(100vh - var(--topnav-height, 90px))' 
    : props.$scroll 
      ? 'auto' 
      : '100vh'};
`;


function App() {
  const auth = useAuth();
  const location = useLocation();
  const [tokenVersion, setTokenVersion] = React.useState(0);
  const [myMembership, setMyMembership] = useState(null);
  const isDesktopState = (() => {
    if (typeof window === 'undefined') return false;
    try {
      const url = new URL(window.location.href);
      const state = url.searchParams.get('state') || '';
      return state.startsWith('desktop:');
    } catch {
      return false;
    }
  })();
  useEffect(() => {
    const bump = () => setTokenVersion(v => v + 1);
    window.addEventListener('auth-tokens-changed', bump);
    window.addEventListener('storage', bump); 
    return () => {
      window.removeEventListener('auth-tokens-changed', bump);
      window.removeEventListener('storage', bump);
    };
  }, []);

  const CHUNK_JOBS_KEY = 'codiii_active_chunk_jobs';

  function readActiveJobs() {
    try {
      return JSON.parse(localStorage.getItem(CHUNK_JOBS_KEY) || '{}');
    } catch {
      return {};
    }
  }

  function writeActiveJobs(jobs) {
    localStorage.setItem(CHUNK_JOBS_KEY, JSON.stringify(jobs || {}));
  }

  function removeActiveJob(jobId) {
    const jobs = readActiveJobs();
    delete jobs[jobId];
    writeActiveJobs(jobs);
  }

  
  const localId   = typeof window !== 'undefined' ? localStorage.getItem('id_token') : null;
  const localAcc  = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null;
  void tokenVersion;
  const hasLocalSession = !!(localId || localAcc);
  const isAuthed = auth.isAuthenticated || hasLocalSession;
  const { refreshTokens } = auth;
  const navRef = useRef(null);
  // Handle exposed by AppTour so the Onboarding Checklist's "Resume tour" button
  // can re-launch the tour from where the user left off.
  const startTourRef = useRef(null);


  useEffect(() => {
    const interval = setInterval(() => {
      refreshTokens();
    }, 50 * 60 * 1000); // every 50 min
    return () => clearInterval(interval);
  }, [refreshTokens]);

  useEffect(() => {
    let cancelled = false;
    async function boot() {
      if (!isAuthed || isDesktopState) {
        if (!cancelled) setBootRefreshDone(true);
        return;
      }
      try {
        await refreshTokens();
      } catch (e) {
        // refresh failed
      } finally {
        if (!cancelled) setBootRefreshDone(true);
      }
    }
    boot();
    return () => { cancelled = true; };
  }, []);

  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const [preferences, setPreferences] = useState(() => {
    const savedPrefs = localStorage.getItem('appPreferences');
    return savedPrefs ? JSON.parse(savedPrefs) : {
    language: 'en',
    theme: 'light',
    wireStyle: 'solid',
    wireHalftone: false
    };
  });
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subscriptionDetails, setSubscriptionDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [isDriveModalOpen, setIsDriveModalOpen] = useState(false);
  const [selectedBlockForDrive, setSelectedBlockForDrive] = useState(null);
  const [showProjectWizard, setShowProjectWizard] = useState(false);
  const [projectWasCreated, setProjectWasCreated] = useState(false);
  const [isUsersOpen, setIsUsersOpen] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(() => {
    const userId = auth.user?.sub;
    if (!userId) return false;
    
    const userKey = `hasVisited_${userId}`;
    const hasVisited = localStorage.getItem(userKey);
    return !hasVisited;
  });
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [chunkingProgress, setChunkingProgress] = useState({
    isVisible: false,
    currentStage: 'Processing Document',
    stageProgress: 0,
    overallProgress: 0,
    documentTitle: 'Document'
  });
  const [projectSettingsDirty, setProjectSettingsDirty] = useState(false);
  const [reviewNavState, setReviewNavState] = useState({ mode: 'browse' });

  const [companyGateLoading, setCompanyGateLoading] = useState(false);
  const [myCompany, setMyCompany] = useState(null);
  const [companyGateError, setCompanyGateError] = useState('');
  const [accessContext, setAccessContext] = useState(null);
  const [accessContextLoading, setAccessContextLoading] = useState(false);
  const [bootRefreshDone, setBootRefreshDone] = useState(false);
  // Once the app has successfully rendered past the boot gate one time, a later
  // re-toggle of accessContextLoading/companyGateLoading (e.g. from a background
  // token-refresh triggering their effects again) is a background revalidation,
  // not a fresh boot — don't tear the whole app down and re-show the spinner for
  // it. Without this, every such re-toggle unmounts/remounts AppContent (and
  // everything inside it, including AppTour), which both flashes a full-screen
  // "refresh" and re-fires tour_started from AppTour's mount effect.
  const hasBootstrappedRef = useRef(false);
  const isSandboxActive = (() => {
    const active = !!accessContext?.isSandboxActive;
    const exp = Number(accessContext?.sandboxExpiresAt || 0);
    if (!active || !exp) return false;
    return (Date.now() / 1000) < exp; 
  })();
  const sandboxMode = isSandboxActive;


  useEffect(() => {
    if (!accessContext?.sandboxExpiresAt) return;
    if (!accessContext?.isSandboxActive) return;

    const expiresMs = Number(accessContext.sandboxExpiresAt) * 1000;
    if (!Number.isFinite(expiresMs)) return;

    const delay = Math.max(0, expiresMs - Date.now() + 750); 

    const t = setTimeout(() => {
      window.dispatchEvent(new Event('auth-tokens-changed'));
    }, delay);

    return () => clearTimeout(t);
  }, [accessContext?.sandboxExpiresAt, accessContext?.isSandboxActive]);

  useEffect(() => {
    phSetActiveProject(selectedProject?.id);
  }, [selectedProject?.id]);

  useEffect(() => {
    phRegister({ projectCount: projects.length });
  }, [projects.length]);



  const userEmail = React.useMemo(() => {
    return auth.user?.profile?.email || auth.user?.email;
  }, [auth.user?.profile?.email, auth.user?.email]);

  const PAYWALL_EXEMPT_EMAILS = new Set([
  ]);
  const COMPANY_RESOURCES_ALLOWLIST = new Set([
    'pr@oquli.ca',
    'dd@oquli.ca',
  ]);

  const normalizedEmail = String(userEmail || '').trim().toLowerCase();
  const isPaywallExemptEmail = PAYWALL_EXEMPT_EMAILS.has(normalizedEmail);
  const canSeeCompanyResources = COMPANY_RESOURCES_ALLOWLIST.has(normalizedEmail);

  useEffect(() => {
    const unsubscribe = documentProgressService.addListener((progressData) => {
      setChunkingProgress(progressData);
    });

    return unsubscribe;
  }, []);

    useEffect(() => {
      let alive = true;

      async function runAccessContext() {
        if (!bootRefreshDone) return;
        if (!isAuthed || isDesktopState) {
          if (!alive) return;
          setAccessContext(null);
          setAccessContextLoading(false);
          return;
        }

        setAccessContextLoading(true);
        try {
          let idToken = localStorage.getItem('id_token');
          if (idToken && isExpired(idToken)) {
            await refreshOnce();
            idToken = localStorage.getItem('id_token');
          }
          if (!idToken) throw new Error('Missing id_token');

          const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000';

          const res = await fetch(`${API_BASE}/api/auth/access-context`, {
            method: 'GET',
            headers: { Authorization: `Bearer ${idToken}` },
          });

          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.detail || `access-context failed (${res.status})`);

          if (!alive) return;
          setAccessContext(data);
        } catch (e) {
          if (!alive) return;
          setAccessContext({ isSandboxActive: false });
        } finally {
          if (!alive) return;
          setAccessContextLoading(false);
        }
      }

      runAccessContext();
      return () => { alive = false; };
    }, [isAuthed, isDesktopState, tokenVersion, location.key, bootRefreshDone]);


    useEffect(() => {
      let alive = true;
      async function postCompanyJson(path, payload) {
        const base = process.env.REACT_APP_COMPANY_API_URL;
        if (!base) throw new Error('Missing REACT_APP_COMPANY_API_URL');

        let idToken = localStorage.getItem('id_token');
        if (idToken && isExpired(idToken)) {
          await refreshOnce();
          idToken = localStorage.getItem('id_token');
        }
        if (!idToken) throw new Error('User is not authenticated');

        const res = await fetch(`${base}${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload || {}),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.message || `Request failed (${res.status})`);
        return data;
      }

      async function claimInvite(companyId) {
        try {
          await postCompanyJson('/company/claim-invite', { companyId });
        } catch (e) {
        }
      }


      async function runCompanyGate() {
        if (!bootRefreshDone) return;

        if (!isAuthed || isDesktopState) {
          if (!alive) return;
          setMyCompany(null);
          setCompanyGateError('');
          setCompanyGateLoading(false);
          return;
        }
        if (isSandboxActive) {
          if (!alive) return;
          setMyCompany(null);
          setMyMembership(null);
          setCompanyGateError('');
          setCompanyGateLoading(false);
          return;
        }


        setCompanyGateLoading(true);
        setCompanyGateError('');

        try {
          const payload1 = await CompanyService.getMyCompany();
          const company1 = payload1?.company ?? payload1 ?? null;

          setMyCompany(company1);
          setMyMembership(payload1?.membership ?? null);

          if (company1?.id) {
            await claimInvite(company1.id);

            const payload2 = await CompanyService.getMyCompany();
            setMyCompany(payload2?.company ?? payload2 ?? null);
            setMyMembership(payload2?.membership ?? null);
          }
        } catch (e) {
          if (!alive) return;
          setCompanyGateError(e?.message || String(e));
        } finally {
          if (!alive) return;
          setCompanyGateLoading(false);
        }
      }

      runCompanyGate();
      return () => { alive = false; };
    }, [isAuthed, isDesktopState, tokenVersion, location.key, isSandboxActive, bootRefreshDone]);


      const membershipStatus = String(myMembership?.status || '').toUpperCase();
      const membershipRole = String(myMembership?.role || '').toUpperCase();
      const isProductRole = membershipRole === 'MEMBER' || membershipRole === 'ADMIN';

      const hasActiveMembership =
        membershipStatus === 'ACTIVE' || membershipStatus === 'INVITED';
      

      const companyStatus = myCompany?.subscriptionStatus || 'none';

      const isCompanySubscribed =
        !!myCompany && (companyStatus === 'active' || companyStatus === 'trialing');

      const isBillingAdminOnly =
        membershipRole === 'BILLING_ADMIN' && hasActiveMembership;

      const canAccessApp =
      isPaywallExemptEmail ||
      isSandboxActive ||
      (isProductRole && hasActiveMembership && isCompanySubscribed);

    useProductUsageHeartbeat(canAccessApp);



    const navigate = useNavigate();

    useEffect(() => {
      if (canAccessApp) {
        const p = location.pathname || '';
        if (p.startsWith('/paywall') || p.startsWith('/billing')) {
          navigate('/', { replace: true });
        }
      }
    }, [canAccessApp, location.pathname, navigate]);

    useLayoutEffect(() => {
      let raf1 = null;
      let raf2 = null;
      let t = null;
      let ro = null;

      const applyHeight = () => {
        const node = navRef.current;
        if (!node) return false;

        const h = node.offsetHeight || 0;
        if (h > 0) {
          document.documentElement.style.setProperty('--topnav-height', `${h}px`);
          return true;
        }
        return false;
      };

      const bindObserver = () => {
        if (!navRef.current) return;
        if (ro) return;

        ro = new ResizeObserver(() => {
          applyHeight();
        });
        ro.observe(navRef.current);
      };

      const ensureMeasured = () => {
        if (applyHeight()) {
          bindObserver();
          return;
        }

        raf1 = requestAnimationFrame(() => {
          if (applyHeight()) {
            bindObserver();
            return;
          }

          raf2 = requestAnimationFrame(() => {
            if (applyHeight()) {
              bindObserver();
            }
          });
        });

        t = setTimeout(() => {
          if (applyHeight()) bindObserver();
        }, 150);

        if (document?.fonts?.ready) {
          document.fonts.ready.then(() => {
            applyHeight();
          }).catch(() => {});
        }
      };

      ensureMeasured();
      window.addEventListener('resize', applyHeight);

      return () => {
        window.removeEventListener('resize', applyHeight);
        if (raf1) cancelAnimationFrame(raf1);
        if (raf2) cancelAnimationFrame(raf2);
        if (t) clearTimeout(t);
        if (ro) ro.disconnect();
      };
    }, [
      isLoading,
      companyGateLoading,
      isAuthed,
      canAccessApp,
    ]);



  const handleSignOut = useCallback(async () => {
    try {
      console.log('Starting signout process...');
      signOutRedirect();
      console.log('Signout initiated successfully');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, []);

  const handleAccount = useCallback(() => {
    if (!subscriptionStatus?.isSubscribed && !subscriptionStatus?.isExempt) {
      setIsSubscriptionOpen(true);
    } else {
      setIsAccountOpen(true);
    }
  }, [subscriptionStatus]);

  const handlePreferencesSave = useCallback((newPreferences) => {
    setPreferences(newPreferences);
    
    document.body.style.backgroundColor = 
      newPreferences.theme === 'dark' ? darkTheme.background : lightTheme.background;
    
    localStorage.setItem('appPreferences', JSON.stringify(newPreferences));
  }, []);

  const handleNewProject = useCallback(() => {
    setShowProjectWizard(true);
  }, []);

  const handleCreateProject = useCallback(async (createdProject) => {
    try {
      console.log('Project created:', createdProject);

      const isFirstProject = projects.length === 0;

      const updatedProjects = await ProjectService.getProjects({
        isAuthenticated: auth.isAuthenticated,
        user: auth.user
      });
      setProjects(updatedProjects);

      setSelectedProject(createdProject);

      phProjectCreated({
        projectId: createdProject?.id,
        projectName: createdProject?.name,
        isFirstProject,
      });

      console.log('Project creation completed successfully');
    } catch (error) {
      console.error('Error handling created project:', error);
      alert('Something went wrong after project creation.');
    }
  }, [auth.isAuthenticated, auth.user, projects]);
  const handleProjectDeleted = useCallback((deletedProjectId) => {
    if (!deletedProjectId) return;

    setProjects(prev => (prev || []).filter(p => p?.id !== deletedProjectId));

    setSelectedProject(prev => (prev?.id === deletedProjectId ? null : prev));

    setCurrentPage('dashboard'); 
  }, []);
  const handleProjectUpdated = useCallback((updatedProject) => {
    if (!updatedProject?.id) return;

    setSelectedProject(updatedProject);

    setProjects(prev => (prev || []).map(p => (p?.id === updatedProject.id ? updatedProject : p)));
  }, []);


  
  const handleSelectProject = useCallback((project) => {
    setSelectedProject(project);
  }, []);


  useEffect(() => {
    let isMounted = true;
  
    const loadProjects = async () => {
      if (!auth.isAuthenticated || !auth.user) {
        return;
      }

      try {
        const loadedProjects = await ProjectService.getProjects({
          isAuthenticated: auth.isAuthenticated,
          user: auth.user
        });
        if (isMounted) {
          setProjects(loadedProjects);
          setProjectsLoaded(true); 
        }
      } catch (error) {
        console.error('Error loading projects:', error);
        if (isMounted) setProjectsLoaded(true); 
      }
    };
  
    if (auth.isAuthenticated && auth.user) {
      loadProjects();
    }
  
    return () => {
      isMounted = false;
    };
  }, [auth.isAuthenticated, auth.user?.sub]);  

  useEffect(() => {
    if (!auth.isLoading && !isDesktopState) {
      setIsLoading(false);
    }
  }, [auth.isLoading, isDesktopState]);
 

  const handleSubscriptionUpgrade = useCallback(async () => {
    if (!auth.user?.email) {
      console.error('No user email found');
      return;
    }

    try {
      await redirectToSubscriptionPage(auth.user.email);
    } catch (error) {
      console.error('Error redirecting to subscription page:', error);
    }
  }, [auth.user?.email]);

  const renderPage = useCallback(() => {
    if (sandboxMode && currentPage !== 'dashboard' && currentPage !== 'requirements') {
      return (
        <div style={{ padding: 40 }}>
          <h2>Sandbox Mode</h2>
          <p>Only Research is available in Sandbox. Upgrade to unlock full access.</p>
        </div>
      );
    }
    switch (currentPage) {
      case 'dashboard':
        return (
          <ProjectDashboard
            onNewProject={handleNewProject}
            onSelectProject={handleSelectProject}
            selectedProject={selectedProject}
            projects={projects}
            currentPage={currentPage}
            sandboxMode={sandboxMode}
          />
        );
      case 'requirements':
        return (
          <RequirementsPage
            onBack={() => setCurrentPage('dashboard')}
            selectedProject={selectedProject}
            isFirstVisit={isFirstVisit}
            projects={projects}
            onOpenDrawingReview={(state) => {
              setReviewNavState(state || { mode: 'mapping' });
              setCurrentPage('review');
            }}
          />
        );
        case 'extracted-requirements':
        return (
          <ExtractedRequirementsPage
            selectedProject={selectedProject}
          />
        );
        case 'company-resources':
          return canSeeCompanyResources ? (
            <CompanyResourcesPage />
          ) : (
            <div style={{ padding: 40 }}>
              <h2>not available</h2>
              <p>company resources is currently limited.</p>
            </div>
          );
      case 'compliance':
        return (
          <ComplianceDashboard
            selectedProject={selectedProject}
            onOpenDrawingReview={(state) => {
              setReviewNavState(state || { mode: 'issue' });
              setCurrentPage('review');
            }}
          />
        );
      case 'communications':
        return (
          <CommunicationsDashboard selectedProject={selectedProject} />
        );
      case 'reconcile-deliverables':
        return (
          <ReconcileDeliverables
            projectId={selectedProject?.id}
            onBack={() => setCurrentPage('requirements')}
          />
        );
      case 'review':
        return (
          <ReviewPage
            selectedProject={selectedProject}
            reviewNavState={reviewNavState}
            onNavigateToLinkFolders={() => setCurrentPage('requirements')}
            onNavigateToCompliance={() => setCurrentPage('compliance')}
          />
        );
        case 'project-settings':
          return (
            <ProjectSettings
              project={selectedProject}
              onBack={() => setCurrentPage('dashboard')}
              projectId={selectedProject?.id}
              auth={auth}  
              onProjectDeleted={handleProjectDeleted} 
              onProjectUpdated={handleProjectUpdated} 
              onTabChange={() => setCurrentPage('dashboard')}
              onDirtyChange={setProjectSettingsDirty}
              planKey={myCompany?.planKey}
            />
          );

      case 'slack-callback':
        return <SlackCallback />;
      case 'subscribe':
        return <Subscription />;
      default:
        return null;
    }
  }, [
    sandboxMode,
    currentPage,
    selectedProject,
    projects,
    isFirstVisit,
    handleNewProject,
    handleSelectProject,
    handleProjectDeleted,
    handleProjectUpdated,  
    canSeeCompanyResources,
    projectSettingsDirty,
    setProjectSettingsDirty,
  ]);


  useEffect(() => {
    if (isFirstVisit && auth.user?.sub) {
      const userKey = `hasVisited_${auth.user.sub}`;
      localStorage.setItem(userKey, 'true');
    }
  }, [isFirstVisit, auth.user?.sub]);

  // Tell the AppTour/OnboardingChecklist persistence layer who's logged in,
  // synchronously off auth.user.sub — NOT off PostHog's distinct_id, which
  // isn't repointed at the real user until ClarityUserProvider's async
  // identify() resolves. Setting this immediately avoids a race on sign-in
  // where AppTour reads progress under the wrong (still-anonymous) key.
  useEffect(() => {
    setOnboardingUserId(auth.user?.sub ?? null);
  }, [auth.user?.sub]);

  function DebugAuth({ auth }) {
  const localId  = typeof window !== 'undefined' ? !!localStorage.getItem('id_token') : false;
  const localAcc = typeof window !== 'undefined' ? !!localStorage.getItem('access_token') : false;
  const hasLocal = localId || localAcc;

  return null; 
}

  useEffect(() => {
  const jobsObj = readActiveJobs();
  const jobsArr = Object.values(jobsObj);
  if (!jobsArr.length) return;

  jobsArr.sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
  const active = jobsArr[0];

  setChunkingProgress({
    isVisible: true,
    currentStage: active.lastMessage || 'Indexing linked documents…',
    stageProgress: Math.max(5, Math.min(99, Number(active.lastProgress) || 5)),
    overallProgress: Math.max(5, Math.min(99, Number(active.lastProgress) || 5)),
    documentTitle: 'Documents'
  });

  documentChunkingProgressService.startPolling({
    jobId: active.jobId,
    statusUrl: process.env.REACT_APP_CHUNK_STATUS_URL,
    onUpdate: (state = {}) => {
      const fallbackProgress = chunkingProgress.overallProgress || 5;
      const progress = Math.max(
        5,
        Math.min(99, Number(state.progress ?? fallbackProgress))
      );

      const message  = state.message ?? chunkingProgress.currentStage ?? 'Indexing linked documents…';

      setChunkingProgress(prev => ({
        ...prev,
        isVisible: true,
        currentStage: message,
        stageProgress: progress,
        overallProgress: progress
      }));

      const fresh = readActiveJobs();
      if (fresh[active.jobId]) {
        fresh[active.jobId].lastProgress = progress;
        fresh[active.jobId].lastMessage  = message;
        writeActiveJobs(fresh);
      }

      documentProgressService.broadcast({
        isVisible: true,
        currentStage: message,
        stageProgress: progress,
        overallProgress: progress,
        documentTitle: 'Documents'
      });

    },
    onDone: () => {
      setChunkingProgress({ isVisible: false, currentStage: '', stageProgress: 0, overallProgress: 0, documentTitle: '' });
      removeActiveJob(active.jobId);
      documentProgressService.broadcast({
        isVisible: false,
        currentStage: '',
        stageProgress: 0,
        overallProgress: 0,
        documentTitle: ''
      });

    },
    onError: () => {
      setChunkingProgress({ isVisible: false, currentStage: '', stageProgress: 0, overallProgress: 0, documentTitle: '' });
      removeActiveJob(active.jobId);
      documentProgressService.broadcast({
        isVisible: false,
        currentStage: '',
        stageProgress: 0,
        overallProgress: 0,
        documentTitle: ''
      });

    }
  });

  return () => {
  };
}, []);  

  if (location.pathname === '/microsoft-admin-consent-complete') {
    return (
      <LanguageProvider>
        <MicrosoftAdminConsentComplete />
      </LanguageProvider>
    );
  }

    return (
    <LanguageProvider>
      <DebugAuth auth={auth} />
      {(() => {
        const bootGateActive = isLoading || accessContextLoading || companyGateLoading || (isAuthed && !isDesktopState && !bootRefreshDone);
        if (!bootGateActive) hasBootstrappedRef.current = true;
        return bootGateActive && !hasBootstrappedRef.current;
      })() ? (
        <LoadingScreen>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
            <path d="M12 4c4.41 0 8 3.59 8 8h2c0-5.52-4.48-10-10-10v2z">
              <animateTransform
                attributeName="transform"
                attributeType="XML"
                type="rotate"
                from="0 12 12"
                to="360 12 12"
                dur="1s"
                repeatCount="indefinite"
              />
            </path>
          </svg>
        </LoadingScreen>
      ) : auth.error ? (
        <ErrorScreen>
          <Logo
            src="/CODiii_PrimaryGradient1.png"
            alt="CODiii Logo"
            style={{ width: '180px' }}
          />
          <ErrorMessage>
            <h2>Authentication Error</h2>
            <p>{auth.error.message}</p>
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          </ErrorMessage>
        </ErrorScreen>
      ) : (!isAuthed || isDesktopState) ? (
        <Routes>
          <Route path="/auth/callback" element={<OidcCallback />} />
          <Route path="/auth/signup" element={<HostedAuth />} />
          <Route path="/auth/confirm" element={<ConfirmSignup />} />
          <Route path="/auth/signin" element={<SignIn />} />
          <Route path="/" element={<Landing />} />
          <Route path="*" element={<Navigate to="/auth/signin" replace />} />
        </Routes>
      ) : isBillingAdminOnly ? (
        <Routes>
          <Route path="*" element={<BillingAdminRedirect />} />
        </Routes>
      ) : (!canAccessApp && !accessContextLoading && !companyGateLoading) ? (
          <Routes>
            <Route path="/paywall" element={<Paywall company={myCompany} />} />


            <Route
              path="/billing/success"
              element={
                <div style={{ padding: 24 }}>
                  <h2>Payment received</h2>
                  <p>Finishing setup… refresh in a moment.</p>
                  <pre style={{ marginTop: 12 }}>
                    {JSON.stringify({ companyGateError, myCompany }, null, 2)}
                  </pre>
                </div>
              }
            />
            <Route
              path="/billing/cancel"
              element={
                <div style={{ padding: 24 }}>
                  <h2>Checkout canceled</h2>
                  <p>You can return to the paywall and try again.</p>
                </div>
              }
            />

            <Route path="*" element={<Navigate to="/paywall" replace />} />
          </Routes>
        ) : (
      <AlertDialogProvider>
        <AppContent
          auth={auth}
          preferences={preferences}
          isPreferencesOpen={isPreferencesOpen}
          setIsPreferencesOpen={setIsPreferencesOpen}
          isAccountOpen={isAccountOpen}
          setIsAccountOpen={setIsAccountOpen}
          isSubscriptionOpen={isSubscriptionOpen}
          setIsSubscriptionOpen={setIsSubscriptionOpen}
          subscriptionStatus={subscriptionStatus}
          subscriptionDetails={subscriptionDetails}
          isLoading={isLoading}
          selectedProject={selectedProject}
          setSelectedProject={setSelectedProject}
          projects={projects}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          isDriveModalOpen={isDriveModalOpen}
          setIsDriveModalOpen={setIsDriveModalOpen}
          selectedBlockForDrive={selectedBlockForDrive}
          setSelectedBlockForDrive={setSelectedBlockForDrive}
          showProjectWizard={showProjectWizard}
          setShowProjectWizard={setShowProjectWizard}
          projectWasCreated={projectWasCreated}
          setProjectWasCreated={setProjectWasCreated}
          isUsersOpen={isUsersOpen}
          setIsUsersOpen={setIsUsersOpen}
          isFirstVisit={isFirstVisit}
          projectsLoaded={projectsLoaded}
          handleSignOut={handleSignOut}
          handleAccount={handleAccount}
          handlePreferencesSave={handlePreferencesSave}
          canSeeCompanyResources={canSeeCompanyResources}
          handleNewProject={handleNewProject}
          handleCreateProject={handleCreateProject}
          handleSubscriptionUpgrade={handleSubscriptionUpgrade}
          renderPage={renderPage}
          chunkingProgress={chunkingProgress}
          navRef={navRef}
          startTourRef={startTourRef}
          sandboxMode={sandboxMode}
          projectSettingsDirty={projectSettingsDirty}
          setProjectSettingsDirty={setProjectSettingsDirty}
          myMembership={myMembership}
          company={myCompany}
          planKey={myCompany?.planKey}
        />
        </AlertDialogProvider>
      )}
    </LanguageProvider>
  );
}

export default App;
